import {ChatHistory} from "./types";
import React, {useEffect, useRef, useState} from "react";
import ReactDOM from 'react-dom';
import {useDispatch, useSelector} from "react-redux";
import {setChatIdForDelete, setPinnedChats} from "../../redux/slices/chat";
import {HiDotsVertical, HiOutlineTrash, HiPencil, HiShare, HiClipboard, HiCheck, HiOutlineChevronRight} from "react-icons/hi";
import { AiOutlinePushpin, AiFillPushpin } from 'react-icons/ai';
import { useChatService } from '../../services';
import { useState as useReactState } from 'react';
import toast from "react-hot-toast";

interface ChatDisplayRowProps {
  history: ChatHistory;
  inputRefs: any;
  onDeleteClick: () => void;
  showEditInput: Record<number, boolean>;
  onToggleEditInput: (record: Record<number, boolean>) => void;
  onPinned?: (chat_id: number, isPinned: boolean) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  onShareChange?: (isShared: boolean, shareToken?: string) => void;
}

const ChatDisplayRow: React.FC<ChatDisplayRowProps> = ({ history, inputRefs, showEditInput, onDeleteClick, onToggleEditInput, onPinned, onMenuOpen, onMenuClose, onShareChange }) => {
  const dispatch = useDispatch();
  const [showToggleAction, setShowToggleAction] = useState<Record<number, boolean>>({});
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [animatedTitle, setAnimatedTitle] = useState(history.title);
  const prevTitleRef = useRef(history.title);
  const { getPinnedChats, togglePinChat, updateChatShareStatus } = useChatService();
  const [isShared, setIsShared] = useReactState<boolean>((history as any)?.is_shared || false);
  const [shareToken, setShareToken] = useReactState<string>((history as any)?.share_token || '');
  const [shareLoading, setShareLoading] = useReactState<boolean>(false);
  const [copiedShare, setCopiedShare] = useReactState<boolean>(false);
  const shareGroupRef = useRef<HTMLDivElement>(null);
  const [openShareLeft, setOpenShareLeft] = useState<boolean>(false);

  const {
    lastActiveChatId
  } = useSelector((state?: any) => state.chatMessages);
  const { pinnedChats } = useSelector((state?: any) => state.chatMessages);

  const closeToggleActionList = () => {
    const wasOpen = Object.keys(showToggleAction).length > 0;
    if (wasOpen && onMenuClose) onMenuClose();
    setShowToggleAction({});
  };

  const handleDeleteClick = (chat_id: number) => {
    onDeleteClick();
    dispatch(setChatIdForDelete(chat_id));
    closeToggleActionList();
  };

  const handleToggleActionList = (chat_id: number) => {
    if (showToggleAction[chat_id]) {
      closeToggleActionList();
      return;
    }
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 140;
      const left = Math.max(8, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8));
      setDropdownPosition({
        top: rect.bottom + 6,
        left
      });
    }
    
    const a: Record<number, boolean> = {};
    a[chat_id] = true;
    setShowToggleAction(a);
    if (onMenuOpen) onMenuOpen();
  };

  const handleChatTitleEdit = (chat_id: number) => {
    const editInputObj: Record<number, boolean> = { ...showEditInput };
    editInputObj[chat_id] = true;

    setTimeout(() => {
      if (inputRefs.current[chat_id]) {
        inputRefs.current[chat_id].focus();
        inputRefs.current[chat_id].select(); // Select all text for easier editing
      }
    }, 0);

    // Remove the project_id dependency - rename should work in all contexts
    if (chat_id) {
      onToggleEditInput(editInputObj);
    }

    closeToggleActionList();
  };

  const [isPinned, setIsPinned] = useState<boolean>((history as any)?.is_pinned || false);

  useEffect(() => {
    const inStore = Array.isArray(pinnedChats) && pinnedChats.some((c: any) => c.chat_id === history.chat_id);
    setIsPinned(inStore || (history as any)?.is_pinned || false);
  }, [pinnedChats, history.chat_id]);

  // Update share state when history changes
  useEffect(() => {
    setIsShared((history as any)?.is_shared || false);
    setShareToken((history as any)?.share_token || '');
  }, [history]);

  const handleTogglePin = async () => {
    const nextPinned = !isPinned;
    closeToggleActionList();
    if (nextPinned && onPinned) onPinned(history.chat_id, nextPinned);

    setIsPinned(nextPinned);
    try {
      const currentPinned: any[] = Array.isArray(pinnedChats) ? pinnedChats : [];
      const optimistic = nextPinned
        ? (currentPinned.some((c: any) => c.chat_id === history.chat_id) ? currentPinned : [history, ...currentPinned])
        : currentPinned.filter((c: any) => c.chat_id !== history.chat_id);
      dispatch(setPinnedChats(optimistic));
    } catch (_) {}
    try {
      await togglePinChat(history.chat_id, nextPinned);
    } catch (_) {
    } finally {
      try { await getPinnedChats(); } catch (_) {}
    }
  };

  const handleToggleShare = async () => {
    const nextShared = !isShared;
    setShareLoading(true);
    
    try {
      const response = await updateChatShareStatus(history.chat_id, nextShared);
      setIsShared(nextShared);
      
      if (nextShared && response.share_token) {
        // Use token from response
        setShareToken(response.share_token);
        if (onShareChange) onShareChange(true, response.share_token);
      } else if (!nextShared) {
        // Clear token when disabling
        setShareToken('');
        if (onShareChange) onShareChange(false, undefined);
      }
    } catch (error) {
      console.error('Failed to toggle share:', error);
    } finally {
      setShareLoading(false);
    }
  };

const handleShareGroupMouseEnter = () => {
  try {
    const container = shareGroupRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const spaceOnRight = window.innerWidth - rect.right;
    const submenuWidthPx = 260; 
    const marginPx = 8;
    setOpenShareLeft(spaceOnRight < submenuWidthPx + marginPx);
  } catch (_) {}
}

  const copyShareLink = async () => {
    if (!shareToken) return;
    
    try {
      const shareUrl = `${window.location.protocol}//${window.location.host}/chat/${shareToken}/share`;
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShare(true);
      
      toast.success('Share link copied!', {
        style: {
          background: 'var(--toast-bg, #ffffff)',
          color: 'var(--toast-text, #000000)',
          border: '1px solid var(--toast-border, #e5e7eb)',
        },
        className: 'dark:!bg-gray-800 dark:!text-white text-sm dark:!border-gray-600',
      });
      
      setTimeout(() => setCopiedShare(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  // Handle click outside and escape key to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(targetNode);
      const clickedOnButton = buttonRef.current && buttonRef.current.contains(targetNode);
      if (clickedInsideDropdown || clickedOnButton) return;
      closeToggleActionList();
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeToggleActionList();
      }
    };

    const handleAnyScroll = () => {
      if (Object.keys(showToggleAction).length > 0) {
        closeToggleActionList();
      }
    };

    // Only add listeners if dropdown is open
    if (Object.keys(showToggleAction).length > 0) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('scroll', handleAnyScroll, true);
      window.addEventListener('resize', handleAnyScroll);
      document.addEventListener('wheel', handleAnyScroll, { passive: true });
      document.addEventListener('touchmove', handleAnyScroll, { passive: true });
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
        window.removeEventListener('scroll', handleAnyScroll, true);
        window.removeEventListener('resize', handleAnyScroll);
        document.removeEventListener('wheel', handleAnyScroll as any);
        document.removeEventListener('touchmove', handleAnyScroll as any);
      };
    }
  }, [showToggleAction]);

  useEffect(() => {
    try {
      if (!showToggleAction[history.chat_id]) return;
      const trigger = buttonRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const dropdownWidth = 140;
      const left = Math.max(8, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8));
      const updatePosition = () => {
        const dropdownEl = dropdownRef.current;
        const dropdownHeight = dropdownEl ? dropdownEl.offsetHeight : 0;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        let top = rect.bottom + 6; 
        if (dropdownHeight + 8 > spaceBelow && spaceAbove >= dropdownHeight + 8) {
          top = rect.top - dropdownHeight - 6;
        }
        top = Math.max(8, Math.min(top, window.innerHeight - dropdownHeight - 8));
        setDropdownPosition({ top, left });
      };
      requestAnimationFrame(updatePosition);
    } catch (_) {}
  }, [showToggleAction, history.chat_id]);

  useEffect(() => {
    const prevTitle = prevTitleRef.current;
    const isNewChatTransition =
      (prevTitle === "New Chat" || !prevTitle) &&
      history.title &&
      history.title !== "New Chat";

    if (isNewChatTransition) {
      let currentIndex = 0;
      setAnimatedTitle('');
      const interval = setInterval(() => {
        currentIndex++;
        setAnimatedTitle(history.title.slice(0, currentIndex));
        if (currentIndex >= history.title.length) {
          clearInterval(interval);
        }
      }, 40);
      prevTitleRef.current = history.title;
      return () => clearInterval(interval);
    } else {
      setAnimatedTitle(history.title);
      prevTitleRef.current = history.title;
    }
  }, [history.title]);

  return (
    <>
       {isShared && (
        <div className="absolute left-1 w-1.5 h-full flex items-center justify-center pointer-events-none transition-opacity duration-200 ">
          <div className="w-0.5 h-3 bg-blue-500 rounded-full" />
        </div>
      )}
      {isPinned && (
        <AiFillPushpin className="w-3.5 h-3.5 text-amber-500 -rotate-90 shrink-0 -mr-2" title="Pinned" />
      )}
      {/* {isShared && (
        <HiShare className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0 -mx-1" title="Shared" />
      )} */}
      <span
        className={`truncate pl-1 flex-grow whitespace-nowrap transition-opacity duration-200 group-hover:pr-8 
          ${history.chat_id === lastActiveChatId ? 'text-blue-800 dark:text-blue-300' : ''}
        `}
        style={{
          maskImage: 'linear-gradient(to right, black 100%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, black 100%, transparent 100%)',
        }}
        {...{
          onMouseEnter: (e: React.MouseEvent<HTMLSpanElement>) => {
            e.currentTarget.style.maskImage = 'linear-gradient(to right, black 70%, transparent 90%)';
            (e.currentTarget.style as any).webkitMaskImage = 'linear-gradient(to right, black 70%, transparent 90%)';
          },
          onMouseLeave: (e: React.MouseEvent<HTMLSpanElement>) => {
            e.currentTarget.style.maskImage = 'linear-gradient(to right, black 100%, transparent 100%)';
            (e.currentTarget.style as any).webkitMaskImage = 'linear-gradient(to right, black 100%, transparent 100%)';
          }
        }}
      >
        {animatedTitle}
      </span>
      
      {/* Share indicator */}
      {/* {isShared && (
        <span className="flex-shrink-0 ml-1 mr-8 text-xs text-blue-500 dark:text-blue-400" title="Shared chat">
          <HiShare className="w-3 h-3" />
        </span>
      )} */}

      <button
        ref={buttonRef}
        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute right-2"
        onClick={(e) => {
          e.stopPropagation();
          if (onMenuOpen) onMenuOpen();
          handleToggleActionList(history.chat_id);
        }}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 bg-transparent">
          <HiDotsVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </div>
      </button>
      {showToggleAction[history.chat_id] && (
        ReactDOM.createPortal(
        <div 
          ref={dropdownRef} 
          className="flex flex-col text-lg  bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-300 fixed rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg"
          style={{
            zIndex: 100000,
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: 140
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleTogglePin();
            }}
            className="text-sm text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center p-2 gap-2 rounded-md"
          >
            {isPinned ? <AiFillPushpin className="text-gray-600 dark:text-gray-400" /> : <AiOutlinePushpin className="text-gray-600 dark:text-gray-400" />} {isPinned ? 'Unpin' : 'Pin'}
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleChatTitleEdit(history.chat_id);
            }}
            className="text-sm text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center p-2 gap-2 rounded-md"
          >
            <HiPencil className="text-gray-600 dark:text-gray-400" /> Rename
          </button>

          {/* Share submenu */}
          <div
            ref={shareGroupRef}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseEnter={() => {handleShareGroupMouseEnter()}}
            className="relative group"
          >
            <button className="text-sm text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center p-2 gap-2 rounded-md w-full text-left">
              <HiShare 
              onClick={(e) => {
                e.stopPropagation();
                handleToggleShare();
              }}
              className={isShared ? "text-blue-600" : "text-gray-600 dark:text-gray-400"} /> Share
              {isShared && (
                <div className="w-2 h-2 bg-blue-500 rounded-full ml-auto"></div>
              )}
            </button>
            
            {/* Share submenu dropdown */}
            <div className={`absolute top-0 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-75 z-50 ${openShareLeft ? 'right-full -mr-1' : 'left-full -ml-1'}`}>
              <div className="p-4">
                <div className="flex justify-start">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    Share this chat
                  </h4>
                </div>

                <div className="space-y-4">
                  {/* Enable Shareable Link Toggle */}
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleShare();
                        }}
                        disabled={shareLoading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800 ${
                          isShared
                            ? 'bg-blue-600 dark:bg-blue-500'
                            : 'bg-gray-200 dark:bg-gray-600'
                        } ${shareLoading ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                            isShared ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        >
                          {shareLoading && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-2.5 w-2.5 border border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          )}
                        </span>
                      </button>
                    </div>
                    <span className="text-sm text-gray-900 dark:text-white">
                      Shareable link
                    </span>
                  </div>

                  {isShared ? (
                    shareToken ? (
                      <div className="space-y-3">
                        {/* Share URL */}
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                            Share URL
                          </span>
                          <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                            <HiShare className="text-gray-500 dark:text-gray-400 flex-shrink-0 text-sm" />
                            <span className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">
                              {`${window.location.host}/chat/${shareToken}/share`}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyShareLink();
                              }}
                              disabled={copiedShare}
                              className="p-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors duration-200 flex-shrink-0"
                            >
                              {copiedShare ? (
                                <HiCheck className="w-3 h-3 text-green-600 dark:text-green-400" />
                              ) : (
                                <HiClipboard className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                        Generating shareable link...
                      </div>
                    )
                  ) : (
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                      Enable shareable link to generate URL
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(history.chat_id);
            }}
            className="text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center p-2 gap-2 rounded-md"
          >
            <HiOutlineTrash /> Delete
          </button>
        </div>, document.body)
      )}
    </>
  )
}

export default ChatDisplayRow;